import React, { useEffect, useMemo, useState } from "react";
import { getUsers, updateUser, deleteUser } from "../../services/userService";
import { getMakeupArtists } from "../../services/makeupArtistService";

interface UserRow {
  id: number;
  username: string;
  fullName: string;
  email?: string;
  phone?: string;
  roleId: number;
  role?: { id: number; name: string };
  makeupArtistId?: number | null;
}

const ROLE_OPTIONS: Array<{ id: number; name: string }> = [
  { id: 1, name: "Admin" },
  { id: 2, name: "Artist" },
];

export default function Users() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Record<number, Partial<UserRow>>>({});
  const [artists, setArtists] = useState<Array<{ id: number; displayName: string }>>([]);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const list = await getUsers();
      const normalized = Array.isArray(list) ? list : (Array.isArray(list?.items) ? list.items : []);
      setUsers(normalized);
    } catch (err: any) {
      setError(err?.response?.data || err?.message || "Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getMakeupArtists().then(setArtists).catch(() => setArtists([]));
  }, []);

  const startEdit = (u: UserRow) => {
    setEditing((prev) => ({
      ...prev,
      [u.id]: {
        fullName: u.fullName || "",
        email: u.email || "",
        phone: u.phone || "",
        roleId: u.roleId,
        makeupArtistId: u.makeupArtistId ?? "" as any,
      },
    }));
  };

  const cancelEdit = (id: number) => {
    setEditing((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const onField = (id: number, field: keyof UserRow, value: any) => {
    setEditing((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const save = async (id: number) => {
    const payload = editing[id];
    if (!payload) return;
    try {
      setError("");
      const send: any = {
        fullName: payload.fullName,
        email: payload.email,
        phone: payload.phone,
        roleId: payload.roleId,
      };
      if (payload.roleId === 1 || payload.makeupArtistId === ("" as any)) {
        // Admin doesn't need a makeupArtistId; drop empty
        // Also drop for empty selection
      } else if (payload.makeupArtistId) {
        send.makeupArtistId = payload.makeupArtistId;
      }
      await updateUser(id, send);
      await load();
      cancelEdit(id);
    } catch (err: any) {
      setError(err?.response?.data || err?.message || "Failed to update user");
    }
  };

  const removeUser = async (id: number) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await deleteUser(id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data || err?.message || "Failed to delete user");
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Manage Users</h1>
        <a href="/register" className="px-3 py-2 rounded bg-pink-600 text-white hover:bg-pink-700">+ Register User</a>
      </div>

      {error && <div className="mb-3 p-2 rounded bg-red-100 text-red-700">{error}</div>}

      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="min-w-full">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Username</th>
              <th className="px-3 py-2 text-left">Full Name</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Phone</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Makeup Artist</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-3 py-3" colSpan={7}>Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td className="px-3 py-3" colSpan={7}>No users found.</td></tr>
            ) : (
              users.map((u) => {
                const e = editing[u.id];
                return (
                  <tr key={u.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{u.username}</td>
                    <td className="px-3 py-2">
                      {e ? (
                        <input className="border rounded px-2 py-1 w-full" value={e.fullName as any} onChange={(ev) => onField(u.id, 'fullName', ev.target.value)} />
                      ) : (
                        u.fullName
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {e ? (
                        <input className="border rounded px-2 py-1 w-full" value={e.email as any} onChange={(ev) => onField(u.id, 'email', ev.target.value)} />
                      ) : (
                        u.email || '-'
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {e ? (
                        <input className="border rounded px-2 py-1 w-full" value={e.phone as any} onChange={(ev) => onField(u.id, 'phone', ev.target.value)} />
                      ) : (
                        u.phone || '-'
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {e ? (
                        <select className="border rounded px-2 py-1 w-full" value={e.roleId as any} onChange={(ev) => onField(u.id, 'roleId', Number(ev.target.value))}>
                          {ROLE_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      ) : (
                        u.role?.name || (u.roleId === 1 ? 'Admin' : u.roleId === 2 ? 'Artist' : `#${u.roleId}`)
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {e ? (
                        <select className="border rounded px-2 py-1 w-full" value={(e.makeupArtistId as any) ?? ''} onChange={(ev) => onField(u.id, 'makeupArtistId', ev.target.value === '' ? '' : Number(ev.target.value))}>
                          <option value="">-- None --</option>
                          {artists.map(a => <option key={a.id} value={a.id}>{a.displayName}</option>)}
                        </select>
                      ) : (
                        (() => {
                          const found = artists.find(a => a.id === u.makeupArtistId);
                          return found ? found.displayName : '-';
                        })()
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      {e ? (
                        <>
                          <button className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700" onClick={() => save(u.id)}>Save</button>
                          <button className="px-3 py-1 rounded bg-gray-300 hover:bg-gray-400" onClick={() => cancelEdit(u.id)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="text-blue-600 hover:text-blue-800" onClick={() => startEdit(u)}>Edit</button>
                          <button className="text-red-600 hover:text-red-800" onClick={() => removeUser(u.id)}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
